import {
	buildLineCompareAnchorFromOrderRange,
	formatComparePeriodLabel,
	formatLineCompareAxisLabel,
	detectLineCompareGranularity,
} from './statisticsComparePresets';
import { TREND_MODE } from './trendMonthlyConstants';
import { shouldUseOrderRangeMonthChartSegments } from './statisticsTrendFilters';
import { isTrendDimensionAxisMode } from './statisticsTrendDimension';
import { isStatisticsMultiYearEnvelopeOrderRange, resolvePeriodQuickPickSelectionLabel } from './statisticsPeriodQuickPickUtils';
import { getStatisticsLensData } from '../../charts/pipeline/lib/orderPrimaryPhaseConfig';

export const STATISTICS_OVERVIEW_COMPARE_NO_DATE_HINT = '选择下单开始与结束日期后显示同比/环比';
export const STATISTICS_COMPARE_ANALYSIS_IRREGULAR_PERIOD_HINT =
	'请通过选期选择整年、整月、整季或快捷区间后再做对比分析';
export const STATISTICS_COMPARE_ANALYSIS_MULTI_YEAR_HINT =
	'多年连续选期（如 2024~2026年）不支持对比段分析，请改用单年或快捷区间';
export const STATISTICS_OVERVIEW_COMPARE_IRREGULAR_PERIOD_HINT = STATISTICS_COMPARE_ANALYSIS_IRREGULAR_PERIOD_HINT;

/** 已配置折线对比（基准 + 对比段/多年份），或维度轴图已有基准 */
const hasPreservedTrendSetup = (filters = {}) => {
	const monthlyYears = filters.trendMonthlyYears;
	if (filters.trendMode === TREND_MODE.YEAR_MONTHS && Array.isArray(monthlyYears) && monthlyYears.length >= 2) {
		return true;
	}
	const hasAnchor = Boolean(filters?.trendAnchor?.startDate && filters?.trendAnchor?.endDate);
	if (!hasAnchor) return false;
	if ((filters?.customComparePeriods?.length ?? 0) > 0 || (monthlyYears?.length ?? 0) > 0) return true;
	return isTrendDimensionAxisMode(filters?.trendAxisMode);
};

/** 总览同比/环比须为规范选期（快捷年月季 / 滚动预设 / 单年整年），手改不规则区间与多年包络不可用 */
export const isStatisticsOverviewComparePeriodEligible = ({ startDate, endDate, trendAnchor = null, datePeriods = null } = {}) => {
	if (!startDate || !endDate) return false;
	if (isStatisticsMultiYearEnvelopeOrderRange(startDate, endDate)) return false;
	const filledExtra =
		Array.isArray(datePeriods) && datePeriods.filter((period) => period?.startDate && period?.endDate).length > 1;
	if (filledExtra) return false;

	const anchor =
		trendAnchor?.startDate && trendAnchor?.endDate ? trendAnchor : { startDate, endDate };

	if (shouldUseOrderRangeMonthChartSegments(anchor)) return false;
	if (Array.isArray(anchor.orderCreatedMonths) && anchor.orderCreatedMonths.length > 0) return false;
	if (Array.isArray(anchor.periods) && anchor.periods.length > 1) return false;

	return Boolean(resolvePeriodQuickPickSelectionLabel(startDate, endDate));
};

/**
 * 对比分析 UI / 编辑回显是否可用。
 * 已保存的折线配置（含本年/滚动区间隔日漂移）须可回显，不能仅因当日选期标签对不上就禁用。
 */
export const isCompareAnalysisFiltersUsable = (filters = {}) => {
	if (isStatisticsMultiYearEnvelopeOrderRange(filters.startDate, filters.endDate)) return false;
	const filledDatePeriods =
		Array.isArray(filters.datePeriods) && filters.datePeriods.filter((period) => period?.startDate && period?.endDate).length > 1;
	/** 多段下单日不能沿用已保存的 year-months，否则主查询会被扩窗并丢掉其余段 */
	if (filledDatePeriods) return false;
	if (hasPreservedTrendSetup(filters)) return true;
	return isStatisticsOverviewComparePeriodEligible({
		startDate: filters.startDate,
		endDate: filters.endDate,
		trendAnchor: filters.trendAnchor,
		datePeriods: filters.datePeriods,
	});
};

export const resolveStatisticsOverviewCompareDisabledHint = ({ startDate, endDate } = {}) => {
	if (!startDate || !endDate) return STATISTICS_OVERVIEW_COMPARE_NO_DATE_HINT;
	if (isStatisticsMultiYearEnvelopeOrderRange(startDate, endDate)) return STATISTICS_COMPARE_ANALYSIS_MULTI_YEAR_HINT;
	return STATISTICS_COMPARE_ANALYSIS_IRREGULAR_PERIOD_HINT;
};

/** 清除折线对比段 / 多年各月模式（保留顶栏日期，基准回退为与下单区间一致） */
export const clearLineCompareFilterFields = (filters = {}) => {
	const next = {
		...filters,
		customComparePeriods: [],
		trendMode: TREND_MODE.PERIODS,
		trendMonthlyYears: [],
		trendCompareAxisUnit: null,
		trendYearSeriesColors: {},
	};
	if (filters.startDate && filters.endDate) {
		next.trendAnchor = buildLineCompareAnchorFromOrderRange(filters.startDate, filters.endDate);
	} else {
		next.trendAnchor = null;
	}
	return next;
};

/** 不规范选期：关闭同比/环比并清空折线对比段配置；规范选期则自动开启同比/环比 */
export const clampCompareAnalysisFiltersForPeriod = (filters = {}) => {
	const overviewEligible = isStatisticsOverviewComparePeriodEligible({
		startDate: filters.startDate,
		endDate: filters.endDate,
		trendAnchor: filters.trendAnchor,
		datePeriods: filters.datePeriods,
	});

	// 已保存/已配置的折线勿因「非整年锚点」或本年/滚动隔日漂移被清掉
	if (isCompareAnalysisFiltersUsable(filters)) {
		if (!overviewEligible) return filters;
		return {
			...filters,
			compareYoy: true,
			compareMom: true,
		};
	}

	return clearLineCompareFilterFields({
		...filters,
		compareYoy: false,
		compareMom: false,
	});
};

export const clampOverviewCompareTogglesForPeriod = clampCompareAnalysisFiltersForPeriod;

/** 总览 / KPI / 主阶段柱状图：仅固定规则的同比、环比 */
const OVERVIEW_COMPARE_KINDS = new Set(['yoy', 'mom']);

export const filterOverviewComparesForLens = (compares = []) => compares.filter((item) => OVERVIEW_COMPARE_KINDS.has(item.kind));

/** V1：对比段始终取 operational */
export const mapComparesForLens = (compares = []) =>
	(compares ?? []).map((item) => ({
		key: item.key,
		kind: item.kind,
		title: item.title,
		periodLabel: item.period?.label ?? '',
		periodStartDate: item.period?.startDate ?? '',
		periodEndDate: item.period?.endDate ?? '',
		lensData: getStatisticsLensData(item) ?? null,
		overview: getStatisticsLensData(item)?.overview ?? null,
	}));

export const resolveCompareDisplayKind = (kind) => {
	if (kind === 'yoy') return 'yoy';
	if (kind === 'mom') return 'mom';
	return 'period';
};

export const resolveCompareBaselineLabel = (compare) => {
	if (compare?.kind === 'anchor') return compare?.title?.trim() || '基准';
	if (compare?.kind === 'yoy') return '去年同期';
	if (compare?.kind === 'mom') return '上期';
	return compare?.title || '对比期';
};

export const resolveCompareDeltaPrefix = (compare) => {
	if (compare?.kind === 'yoy') return '同比';
	if (compare?.kind === 'mom') return '环比';
	return '对比';
};

const resolveLensMetricRow = (lensData, selectedPhase) => {
	if (!lensData) return null;
	if (!selectedPhase) return lensData.overview ?? null;
	return lensData.phases?.find((row) => row.phase === selectedPhase) ?? null;
};

/** 折线图 X 轴：优先自定义名称，其次整月/整季简称，最后日期区间 */
export const resolveTimelineAxisLabel = ({ kind, title, periodLabel, startDate, endDate }) => {
	const name = title?.trim();
	const periodText = periodLabel?.trim();
	const friendly = startDate && endDate ? formatComparePeriodLabel(startDate, endDate) : '';

	if (name && periodText && name !== periodText) return name;
	if (name && !periodText) return name;
	if (friendly) return friendly;
	if (name) return name;
	if (periodText) return periodText;
	if (kind === 'current') return '本期';
	if (kind === 'anchor') return title?.trim() || '基准';
	return '对比期';
};

/** 主查询本期：制作区默认预览用统计结果，不另起数据集 */
export const buildCurrentStatisticsComparePoint = (primaryPhaseStats = null) => {
	const lensData = getStatisticsLensData(primaryPhaseStats);
	if (!lensData) return null;
	const period = primaryPhaseStats?.currentPeriod ?? null;
	const label = period?.label?.trim() || '当前筛选';
	return {
		key: 'current',
		kind: 'current',
		title: label,
		periodLabel: label,
		periodStartDate: period?.startDate ?? '',
		periodEndDate: period?.endDate ?? '',
		lensData,
		overview: lensData.overview ?? null,
	};
};

const isLineCompareTimelineKind = (kind) => kind === 'anchor' || kind === 'custom' || kind === 'current';

const buildLineCompareTimelinePoint = (compare, selectedPhase, getMetricValue, metrics = []) => {
	if (!isLineCompareTimelineKind(compare.kind)) return null;
	if (compare.kind !== 'current' && !compare.periodStartDate) return null;

	const row = resolveLensMetricRow(compare.lensData, selectedPhase);
	const granularity =
		compare.periodStartDate && compare.periodEndDate
			? detectLineCompareGranularity(compare.periodStartDate, compare.periodEndDate)
			: null;
	const axisLabel =
		granularity && compare.periodStartDate && compare.periodEndDate
			? formatLineCompareAxisLabel(compare.periodStartDate, compare.periodEndDate, granularity)
			: resolveTimelineAxisLabel({
					kind: compare.kind,
					title: compare.title,
					periodLabel: compare.periodLabel,
					startDate: compare.periodStartDate,
					endDate: compare.periodEndDate,
				});

	const base = {
		key: compare.key,
		kind: compare.kind,
		title: compare.title,
		label: compare.title || compare.periodLabel || (compare.kind === 'anchor' ? '基准' : compare.kind === 'current' ? '当前筛选' : '对比期'),
		periodLabel: compare.periodLabel || compare.title || compare.periodStartDate,
		startDate: compare.periodStartDate,
		endDate: compare.periodEndDate,
		axisLabel,
	};

	if (metrics.length === 1) {
		return { ...base, value: getMetricValue(row, metrics[0]) };
	}

	const valuesByMetric = Object.fromEntries(metrics.map((metric) => [metric, getMetricValue(row, metric)]));
	return { ...base, valuesByMetric, value: valuesByMetric[metrics[0]] ?? 0 };
};

const buildLineCompareTimelinePoints = ({ compares = [], selectedPhase, getMetricValue, metrics = [] }) => {
	if (!getMetricValue || !metrics.length) return [];

	const points = [];
	for (const compare of compares) {
		const point = buildLineCompareTimelinePoint(compare, selectedPhase, getMetricValue, metrics);
		if (point) points.push(point);
	}

	return points.sort((a, b) => String(a.startDate || '').localeCompare(String(b.startDate || '')));
};

/** 折线图：基准/自定义对比段；无对比时用主查询本期（kind=current） */
export const buildStatisticsLineCompareTimeline = ({ compares = [], activeMetric, selectedPhase, getMetricValue }) =>
	buildLineCompareTimelinePoints({
		compares,
		selectedPhase,
		getMetricValue,
		metrics: activeMetric ? [activeMetric] : [],
	});

/** 折线对比 · 多指标时间轴（各点含 valuesByMetric） */
export const buildStatisticsLineCompareMultiMetricTimeline = ({ compares = [], metrics = [], selectedPhase, getMetricValue }) =>
	buildLineCompareTimelinePoints({ compares, selectedPhase, getMetricValue, metrics });
